import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

/**
 * OAuth Success Page
 * 
 * Handles OAuth callback redirect and sends result to parent window via postMessage.
 * This page is opened in a popup window during OAuth flow.
 */
const OAuthSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    // Extract callback parameters from URL
    const gscStatus = searchParams.get('gsc_status');
    const ga4Status = searchParams.get('ga4_status');
    const bingStatus = searchParams.get('bing_status');
    const pinterestStatus = searchParams.get('pinterest_status');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    // Determine provider and status
    let provider = 'unknown';
    let isSuccess = false;

    if (gscStatus) {
      provider = 'google';
      isSuccess = gscStatus === 'success';
    } else if (ga4Status) {
      provider = 'google';
      isSuccess = ga4Status === 'success';
    } else if (bingStatus) {
      provider = 'bing';
      isSuccess = bingStatus === 'success';
    } else if (pinterestStatus) {
      provider = 'pinterest';
      isSuccess = pinterestStatus === 'success';
    }

    // Set UI status
    if (error) {
      setStatus('error');
      setMessage(errorDescription || error);
    } else if (isSuccess) {
      setStatus('success');
      setMessage(`${provider.toUpperCase()} connected successfully`);
    } else {
      setStatus('error');
      setMessage('OAuth authorization failed');
    }

    // Send message to parent window (opener)
    if (window.opener) {
      window.opener.postMessage(
        {
          type: 'oauth-callback',
          status: isSuccess ? 'success' : 'error',
          provider,
          message: error ? errorDescription || error : `${provider} connected successfully`,
        },
        window.location.origin
      );

      // Auto-close popup after 2 seconds
      setTimeout(() => {
        window.close();
      }, 2000);
    }
  }, [searchParams]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      padding={3}
    >
      {status === 'processing' && (
        <>
          <CircularProgress size={60} />
          <Typography variant="h6" marginTop={2}>
            Processing OAuth callback...
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircleIcon color="success" sx={{ fontSize: 80 }} />
          <Typography variant="h5" marginTop={2} color="success.main">
            Authorization Successful
          </Typography>
          <Typography variant="body1" marginTop={1} color="text.secondary">
            {message}
          </Typography>
          <Typography variant="body2" marginTop={2} color="error.main" fontWeight={700} textAlign="center" maxWidth={520}>
            Review the connected properties and select the one property you want to use for SEO reports. If multiple properties were found, only one is active by default and the others are disabled.
          </Typography>
          <Typography variant="body2" marginTop={2} color="text.secondary">
            This window will close automatically...
          </Typography>
        </>
      )}

      {status === 'error' && (
        <>
          <ErrorIcon color="error" sx={{ fontSize: 80 }} />
          <Typography variant="h5" marginTop={2} color="error.main">
            Authorization Failed
          </Typography>
          <Alert severity="error" sx={{ marginTop: 2, maxWidth: 500 }}>
            {message}
          </Alert>
          <Typography variant="body2" marginTop={2} color="text.secondary">
            This window will close automatically...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default OAuthSuccessPage;
